import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ShoppingCart, ChevronLeft, ChevronRight, Tag, Heart, ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Cart from '../components/Cart';
import WhatsAppButton from '../components/WhatsAppButton';
import OfferModal from '../components/OfferModal';
import FrameSelector from '../components/FrameSelector';
import ShareButtons from '../components/ShareButtons';
import AskOnWhatsApp from '../components/AskOnWhatsApp';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { useFavorites } from '../context/FavoritesContext';
import { formatDimensions, formatUSD } from '../utils/format';
import { trackArtworkView, trackPageView } from '../utils/track';
import '../components/Works.css';
import './ArtworkDetail.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = (typeof window !== 'undefined' && window.location.origin) || '';

// Update / restore document head meta tags for social sharing (Pinterest, OG, Twitter)
const setMeta = (attr, key, value) => {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
};

const setCanonical = (url) => {
  if (typeof document === 'undefined') return;
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', url);
};

const ArtworkDetail = ({ categoryHint }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { t, lang } = useLanguage();
  const { isFavorite, toggleFavorite } = useFavorites();

  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedFrame, setSelectedFrame] = useState('black');
  const [showOfferModal, setShowOfferModal] = useState(false);

  // Fetch artwork by slug
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    fetch(`${BACKEND_URL}/api/artworks/slug/${encodeURIComponent(slug)}`, { cache: 'no-store' })
      .then((res) => {
        if (!res.ok) throw new Error('not-found');
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        setArtwork(data);
        trackArtworkView(data);
        trackPageView();
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [slug]);

  // Update document title + meta tags for Pinterest Rich Pins + Open Graph
  useEffect(() => {
    if (!artwork) return;
    const url = `${SITE_URL}/${categoryHint === 'sculpture' ? 'white-dolls' : 'works'}/${slug}`;
    const title = `${artwork.title} — C. LLAGUNO`;
    const desc = `${artwork.title} · ${artwork.medium || ''} · ${artwork.dimensions || ''}${artwork.price ? ` · ${formatUSD(artwork.price)} USD` : ''}. Contemporary art by C. LLAGUNO.`;
    // Serve a real https image (backend decodes the base64 data URI in Mongo)
    // so Pinterest / Facebook / WhatsApp crawlers can fetch it.
    const image = `${BACKEND_URL}/api/artworks/image/${slug}`;

    document.title = title;
    setMeta('name', 'description', desc);
    setCanonical(url);

    // Open Graph
    setMeta('property', 'og:type', 'product');
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', desc);
    setMeta('property', 'og:url', url);
    if (image) setMeta('property', 'og:image', image);
    setMeta('property', 'og:site_name', 'C. LLAGUNO');

    // Twitter
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', desc);
    if (image) setMeta('name', 'twitter:image', image);

    // Pinterest Rich Pins (product) - additional hints
    if (artwork.price) {
      setMeta('property', 'product:price:amount', String(artwork.price));
      setMeta('property', 'product:price:currency', 'USD');
      setMeta('property', 'og:price:amount', String(artwork.price));
      setMeta('property', 'og:price:currency', 'USD');
    }
    setMeta('property', 'product:availability', artwork.available ? 'in stock' : 'out of stock');
  }, [artwork, slug, categoryHint]);

  const getImagesArray = (art) => {
    if (!art) return [];
    const base = [
      { url: art.image, label: art.category === 'sculpture' ? t('label_full_sculpture') : t('label_full_artwork') },
      { url: art.scene, label: t('label_in_scene') },
    ];
    if (art.category !== 'sculpture' && art.detail) {
      base.splice(1, 0, { url: art.detail, label: t('label_detail') });
    }
    return base.filter((i) => !!i.url);
  };

  const images = getImagesArray(artwork);

  const nextImage = () => {
    if (images.length) setCurrentImageIndex((p) => (p + 1) % images.length);
  };
  const prevImage = () => {
    if (images.length) setCurrentImageIndex((p) => (p - 1 + images.length) % images.length);
  };

  const handleAddToCart = () => {
    if (artwork?.available) {
      addToCart(artwork, artwork.category === 'sculpture' ? undefined : { frame: selectedFrame });
    }
  };

  const backHref = artwork
    ? (artwork.category === 'sculpture' ? '/#sculptures' : '/#works')
    : '/';

  return (
    <div className="artwork-detail-wrapper">
      <Header />

      <main className="artwork-detail-page">
        <div className="artwork-detail-container">
          <button
            className="artwork-back-link"
            onClick={() => navigate(backHref)}
            data-testid="back-to-home-btn"
          >
            <ArrowLeft size={16} /> {t('back_to_home')}
          </button>

          {loading && (
            <p className="artwork-detail-loading">{t('loading_artwork')}</p>
          )}

          {!loading && notFound && (
            <div className="artwork-detail-empty">
              <p>{t('artwork_not_found')}</p>
              <Link to="/" className="artwork-back-link">{t('back_to_home')}</Link>
            </div>
          )}

          {!loading && artwork && (
            <div className="artwork-detail-content">
              <div className="modal-grid">
                <div className="modal-gallery">
                  <div className="modal-image-container">
                    <img src={images[currentImageIndex]?.url} alt={artwork.title} />
                    {images.length > 1 && (
                      <>
                        <button className="gallery-nav prev" onClick={prevImage} data-testid="detail-gallery-prev">
                          <ChevronLeft size={24} />
                        </button>
                        <button className="gallery-nav next" onClick={nextImage} data-testid="detail-gallery-next">
                          <ChevronRight size={24} />
                        </button>
                      </>
                    )}
                  </div>
                  {images.length > 1 && (
                    <div className={`gallery-thumbnails ${images.length === 2 ? 'gallery-thumbnails-2' : ''}`}>
                      {images.map((img, idx) => (
                        <div
                          key={idx}
                          className={`thumbnail ${currentImageIndex === idx ? 'active' : ''}`}
                          onClick={() => setCurrentImageIndex(idx)}
                          data-testid={`detail-thumbnail-${idx}`}
                        >
                          <img src={img.url} alt={img.label} />
                          <span className="thumbnail-label">{img.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="modal-details">
                  <div className="detail-header-row">
                    <div className={`modal-status ${artwork.available ? 'available' : 'sold'}`}>
                      {artwork.available ? t('badge_available') : t('badge_sold')}
                    </div>
                    <button
                      className={`favorite-btn favorite-btn-inline ${isFavorite(artwork.id) ? 'active' : ''}`}
                      onClick={() => toggleFavorite(artwork.id)}
                      aria-label="Save to favorites"
                      data-testid={`detail-fav-${artwork.id}`}
                    >
                      <Heart size={18} fill={isFavorite(artwork.id) ? '#000' : 'none'} />
                    </button>
                  </div>

                  <h1 className="artwork-detail-title" translate="no">{artwork.title}</h1>
                  <p className="modal-year">{artwork.year}</p>
                  <p className="modal-medium">{artwork.medium}</p>
                  <p className="modal-dimensions" translate="no">
                    {formatDimensions(artwork.dimensions, lang)}
                  </p>

                  {artwork.available && (
                    <>
                      <p className="modal-price" translate="no">{formatUSD(artwork.price)} USD</p>
                      <ul className="modal-details-list">
                        <li className="detail-shipping">{t('detail_shipping')}</li>
                        {artwork.category !== 'sculpture' && <li>{t('detail_ready')}</li>}
                        <li>{t('detail_signed')}</li>
                        <li>{t('detail_certificate')}</li>
                      </ul>

                      {artwork.category !== 'sculpture' && (
                        <FrameSelector value={selectedFrame} onChange={setSelectedFrame} testId="detail-frame" />
                      )}

                      <Button
                        onClick={handleAddToCart}
                        className="add-to-cart-button"
                        data-testid="detail-add-to-cart-btn"
                      >
                        <ShoppingCart size={20} />
                        {t('add_to_cart')}
                      </Button>

                      <AskOnWhatsApp title={artwork.title} testId="detail-ask-whatsapp-btn" />

                      {artwork.price > 2500 && (
                        <button
                          onClick={() => setShowOfferModal(true)}
                          className="make-offer-button"
                          data-testid="detail-make-offer-btn"
                        >
                          <Tag size={18} />
                          {t('make_offer')}
                        </button>
                      )}
                    </>
                  )}

                  <ShareButtons artwork={artwork} />
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
      <Cart />
      <WhatsAppButton />

      {showOfferModal && artwork && (
        <OfferModal artwork={artwork} onClose={() => setShowOfferModal(false)} />
      )}
    </div>
  );
};

export default ArtworkDetail;
